import { Popover } from '@headlessui/react';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { Button as EditorButton } from '~components/generic/editor/Button/Button';
import ModulesList from '~components/generic/module/ModulesList/ModulesList';
import { PlusIcon } from '~components/icons';
import styles from './ModulesListPopoverPortal.css';

type ModulesListPopoverProps = {
  disabled?: boolean;
  index: number;
  parentContext: string;
  type: string;
};

export function ModulesListPopoverPortal({ disabled = false, index, parentContext, type }: ModulesListPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const popoverButtonRef = useRef(null);
  const ref = useRef(null);

  const openPopover = () => {
    setIsOpen(true);
    calculatePopoverPosition();
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closePopover = () => {
    setIsOpen(false);
  };

  const calculatePopoverPosition = () => {
    if (popoverButtonRef.current) {
      const buttonRect = popoverButtonRef.current.getBoundingClientRect();
      const { top, left } = buttonRect;
      setPopoverPosition({ top: top + buttonRect.height, left });
    }
  };

  const portalContainer = isOpen && (
    <div
      ref={ref}
      className={clsx(styles.panel, 'absolute z-50 transform left-0')}
      style={{ top: popoverPosition.top, left: popoverPosition.left }}
    >
      <ModulesList onChooseItem={() => closePopover()} type={type} parentContext={parentContext} index={index} />
    </div>
  );

  return (
    <div className={clsx(styles.popover, 'modules-list-popover', 'relative inline-flex')}>
      <Popover>
        {({ open }) => (
          <>
            <div className={clsx(styles.createModule)} ref={popoverButtonRef}>
              <Popover.Button
                as={EditorButton}
                preIcon={<PlusIcon />}
                disabled={disabled || open}
                onClick={openPopover}
              />
              <hr className={styles.moduleLine} />
            </div>

            {isOpen && ReactDOM.createPortal(portalContainer, document.body)}
          </>
        )}
      </Popover>
    </div>
  );
}
